import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, IconButton, Modal, Typography } from "@mui/material";

import { User } from "../../../types/types";

import { Bucket } from "../enum/jobEnums";
import { CloseButtonIcon } from "../assets/icons/components-icons";
import QualifyCandidateFigure from "../assets/images/qualify-candidate-figure.png";

import { AddCandidateReviewModal, useAddCandidateReviewModal } from "./AddCandidateReviewModal";

export const QualifyCandidateModal = ({ open, onClose, user }: { open: boolean; onClose: () => void; user: User }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "components.qualifyCandidateModal" });
  const {
    openAddCandidateReviewModal,
    handleOpenAddCandidateReviewModal,
    handleCloseAddCandidateReviewModal,
    handleAddCandidateReview,
  } = useAddCandidateReviewModal(user, Bucket.QUALIFIED);

  const handleQualifyCandidate = () => {
    handleOpenAddCandidateReviewModal();
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={styles.container}>
          <Box sx={styles.header}>
            <Typography sx={styles.headerTitle}>{t("qualifyCandidate")}</Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseButtonIcon />
            </IconButton>
          </Box>
          <Divider flexItem orientation="horizontal" />
          <Box sx={styles.content}>
            <Box component="img" src={QualifyCandidateFigure} sx={styles.contentFigure} />
            <Typography sx={styles.contentTitle}>{t("qualifyCandidate")}</Typography>
            <Box sx={styles.contentDescriptionContainer}>
              <Typography sx={styles.contentDescription}>{t("qualifyCandidateDescription1")}</Typography>
              <Typography sx={styles.contentDescription}>{t("qualifyCandidateDescription2")}</Typography>
            </Box>
          </Box>
          <Divider flexItem orientation="horizontal" />
          <Box sx={styles.buttonContainer}>
            <Button onClick={onClose} variant="outlined-md" sx={styles.cancelButton}>
              {t("cancel")}
            </Button>
            <Button onClick={handleQualifyCandidate} variant="primary-md">
              {t("addReviewContinue")}
            </Button>
          </Box>
        </Box>
      </Modal>
      <AddCandidateReviewModal
        bucket={Bucket.QUALIFIED}
        open={openAddCandidateReviewModal}
        onClose={handleCloseAddCandidateReviewModal}
        handleAddCandidateReview={handleAddCandidateReview}
        candidate={user}
      />
    </>
  );
};

const styles = {
  container: {
    width: "640px",
    height: "fit-content",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 24px 0 24px",
  },
  headerTitle: {
    fontFamily: "Montserrat",
    color: "var(--colors-text-text-primary-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
  },
  contentFigure: {
    width: "166px",
    height: "125px",
  },
  contentTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  contentDescription: {
    fontFamily: "Montserrat",
    color: "var(--Dark---800, #414141)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  contentDescriptionContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "0 24px 24px",
  },
  disregardButton: {
    background: "var(--Error-700, #B42318) !important",
    color: "var(--Base-White, #FFF) !important",
    borderColor: "var(--Error-700, #B42318) !important",
  },
  cancelButton: {
    background: "var(--Base-White, #FFF) !important",
    color: "var(--Gray-700, #344054) !important",
    borderColor: "var(--Gray-300, #D0D5DD) !important",
  },
};

export const useQualifyCandidateModal = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return {
    openQualifyCandidateModal: open,
    handleOpenQualifyCandidateModal: handleOpen,
    handleCloseQualifyCandidateModal: handleClose,
  };
};
